import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import "./Dashboard.css";
import "../styles/main.css";
import { apiGet, apiPost } from "../services/api";
import { toast } from "react-toastify";
import ActivateTaskModal from "../components/modals/ActivateTaskModal";
import { useToast } from "../hooks/useToast";
import { Alert, Badge, Form, Spinner } from "react-bootstrap";
import CommentsModal from "../components/modals/CommentsModal";
import AttachmentsModal from "../components/modals/AttachmentsModal";
import DeadlineHistoryModal from "../components/modals/DeadlineHistoryModal";
import ActionCommentsModal from "../components/modals/ActionCommentsModal";
import AttachmentsModalTrash from "../components/modals/AttachmentsModalTrash";
import { formatDate } from "../utils/dateUtils";
import { Button } from "react-bootstrap";

const Trash = () => {
  const BASE_ENDPOINT = "/TaskTracking";
  const [isLoading, setIsLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const { showToast, hideToast, toastConfig } = useToast();
  const [taskID, setTaskID] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [commentsModal, setCommentsModal] = useState({
    show: false,
    taskId: null,
    type: "officer",
    officerName: "",
    coordinatorName: "",
  });

  const [expandedRows, setExpandedRows] = useState({});
  
  const toggleRowExpand = (taskID, field) => {
    setExpandedRows(prev => ({
      ...prev,
      [taskID]: {
        ...prev[taskID],
        [field]: !(prev[taskID]?.[field] || false)
      }
    }));
  };

  const [attachmentsModal, setAttachmentsModal] = useState({
    show: false,
    taskId: null,
    officerName: "",
  });

  const [deadlineHistoryModal, setDeadlineHistoryModal] = useState({
    visible: false,
    loading: false,
    deadlines: [],
    currentTaskId: null,
  });

  const [actionCommentsModal, setActionCommentsModal] = useState({
    show: false,
    taskId: null,
    comments: [],
    loading: false,
    coordinatorName: "",
  });

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      
      const voices = window.speechSynthesis.getVoices();
      const femaleVoice = voices.find(voice => 
        voice.name.includes('Female') || 
        voice.name.includes('Woman') ||
        voice.name.includes('Zira') ||  
        voice.name.includes('Samantha')  
      );
      
      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }
      
      utterance.rate = 0.9;
      utterance.pitch = 1.1;
      utterance.volume = 1;
      
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    }
  };

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      const response = await apiGet(`${BASE_ENDPOINT}/GetInactiveTasks`);

      if (response?.isSuccess) {
        const sortedTasks = response.data.sort(
          (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
        );
        setTasks(sortedTasks);
      } else {
        throw new Error(response?.message || "Failed to fetch tasks");
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowDeadlineHistory = async (taskId) => {
    try {
      setDeadlineHistoryModal((prev) => ({
        ...prev,
        visible: true,
        loading: true,
        currentTaskId: taskId,
      }));

      const response = await apiGet(`/TaskTracking/task-deadlines/${taskId}`);

      if (response?.isSuccess && response.data) {
        const sortedDeadlines = response.data.sort(
          (a, b) => new Date(b.changeDate) - new Date(a.changeDate)
        );

        setDeadlineHistoryModal((prev) => ({
          ...prev,
          deadlines: sortedDeadlines,
          loading: false,
        }));
      } else {
        setDeadlineHistoryModal((prev) => ({ ...prev, loading: false }));
        toast.error(response?.message || "No deadline history found");
      }
    } catch (error) {
      console.error("Error fetching deadline history:", error);
      setDeadlineHistoryModal((prev) => ({ ...prev, loading: false }));
      toast.error("Failed to load deadline history");
    }
  };

  const handleShowCoordinatorComments = (task) => {
    setCommentsModal({
      show: true,
      taskId: task.taskID,
      type: "coordinator",
      coordinatorName: task.coordinatorNames || "Coordinator",
      officerName: "",
    });
  };

  const handleShowOfficerComments = (task) => {
    setCommentsModal({
      show: true,
      taskId: task.taskID,
      type: "officer",
      officerName: task.officerNames || "Officer",
      coordinatorName: "",
    });
  };

  const handleShowAttachments = (task) => {
    setAttachmentsModal({
      show: true,
      taskId: task.taskID,
      officerName: task.officerNames || "Officer",
    });
  };

  const handleActivate = async (comment) => {
    if (taskID === null) {
      showToast("TaskID needed", "error");
      return;
    }
    try {
      setIsLoading(true);
      const payload = {
        TaskID: taskID,
        Comment: comment,
      };
      const response = await apiPost(`${BASE_ENDPOINT}/RetrieveTask`, payload);
      
      if (response?.isSuccess) {
        showToast("Task retrieved successfully!", "success");
        speak("Task retrieved successfully");
        fetchTasks();
      } else {
        throw new Error(response?.message || "Failed to retrieve task");
      }
    } catch (error) {
      showToast(error.message || "Something went wrong", "error");
      speak("Failed to retrieve task");
    } finally {
      setIsLoading(false);
      setTaskID(null);
      setShowModal(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    
    // Welcome message with female voice
    const welcomeMessage = "Welcome to Trash";
    setTimeout(() => {
      speak(welcomeMessage);
    }, 1000);

    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = () => {
        // Voices are loaded
      };
    }
  }, []);

  const columns = [
    {
      name: "Task ID",
      selector: (row) => row.taskID,
      sortable: true,
     width: '110px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
        name: <div className="text-center w-100">Issue/Action/Decision </div>,
      selector: (row) => row.issueActionOrDecision,
      sortable: true,
      wrap: true,
      cell: (row) => {
        const text = row.issueActionOrDecision || "";
        const isExpanded = expandedRows[row.taskID]?.issueExpanded || false;
        const showToggle = text.length > 100;
        
        return (
          <div style={{ textAlign: "left" }}>
            <div style={{ whiteSpace: "pre-wrap" }}>
              {isExpanded ? text : text.slice(0, 100)}
              {text.length > 100 && !isExpanded && "..."}
            </div>
            {showToggle && (
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 view-more-text"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRowExpand(row.taskID, 'issueExpanded');
                }}
              >
                {isExpanded ? "View Less" : "View More"}
              </Button>
            )}
          </div>
        );
      },
        width: '230px',
 
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: "Action Taken / Status",
      wrap: true,
      selector: (row) => row.actionTakenOrStatus,
     cell: (row) => {
  const hasComments = row.actionCount > 0;

  return (
    <div
      className="d-flex align-items-start text-start"
      style={{ width: "100%" }}
    >
      <span
        style={{
          flex: 1,
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left"
        }}
      >
        {row.actionTakenOrStatus || "--"}
      </span>

      {hasComments && (
        <i
          className="bi bi-chat-text ms-2 text-muted cursor-pointer mediumcolor"
          onClick={(e) => {
            e.stopPropagation();
            handleShowActionComments(row);
          }}
          title="View comments"
        />
      )}
    </div>
  );
},

        width: '220px',
 
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: "Priority",
      selector: (row) => row.priorityName,
      cell: (row) => {
        let bg = "transparent", color = "#1e293b";

        switch (row.priorityName?.toLowerCase()) {
          case "top":
            color = "#92400e"; break;
          case "high":
            color = "#7f1d1d"; break;
          case "medium":
             color = "#1e3a8a"; break;
        }

        return (
          <span
            style={{
              display: "inline-block",
              padding: "4px 12px",
              borderRadius: "999px",
              backgroundColor: bg,
              color: color,
              fontWeight: "600",
              fontSize: "13px",
              textTransform: "capitalize",
            }}
          >
            {row.priorityName}
          </span>
        );
      },
      sortable: true,
        width: '140px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    
    {
      name: "Decision Date",
      selector: (row) => formatDate(row.dateOfDecisionOrInitiation),
      sortable: true,
        width: '17s0px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: "Deadline",
      selector: (row) => formatDate(row.deadline),
      cell: (row) => {
        const hasDeadline = row.deadlineCount > 0;
        return (
          <div className="d-flex align-items-center">
            <span
              className={hasDeadline ? "mediumcolor cursor-pointer" : ""}
              onClick={() =>
                hasDeadline && handleShowDeadlineHistory(row.taskID)
              }
            >
              {formatDate(row.deadline)}
            </span>
            {hasDeadline && (
              <i
                className="bi bi-clock-history ms-2 text-muted cursor-pointer mediumcolor"
                onClick={() => handleShowDeadlineHistory(row.taskID)}
                title="View deadline history"
              />
            )}
          </div>
        );
      },
      sortable: true,
        width: '140px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
    
         name: <div className="text-center w-100">Coordinators</div>,
      selector: (row) => row.coordinatorNames || "--",
      wrap: true,
        width: '170px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
      textAlign:'left'
    },
    },
    {
      name: <div style={{ width: '100%', textAlign: 'center' }}>Officers</div>  ,
      wrap: true,
      selector: (row) => row.officerNames || "--",
      cell: (row) => {
        const hasAttachments = row.attachmentCount > 0;
        const hasComments = row.remarksCount > 0;

        return (
          <div className="d-flex align-items-center">
            <span>{row.officerNames || "--"}</span>

            {hasComments && (
              <i
                className="bi bi-chat-text ms-2 text-muted cursor-pointer mediumcolor"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowOfficerComments(row);
                }}
                title="View comments"
              />
            )}

            {hasAttachments && (
              <i
                className="bi bi-paperclip ms-2 text-muted cursor-pointer mediumcolor"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowAttachments(row);
                }}
                title="View attachments"
              />
            )}
          </div>
        );
      },
      wrap: true,
        width: '180px',

    style: {
      borderRight: '1px solid #dee2e6',
      textAlign:'left'
    },
    },
    {
         name: <div className="text-center w-100">Additional Comments  </div>,
      selector: (row) => row.additionalComments,
      sortable: true,
      wrap: true,
      cell: (row) => {
        const text = row.additionalComments || "";
        const isExpanded = expandedRows[row.taskID]?.commentsExpanded || false;
        const showToggle = text.length > 100;

        return (
          <div>
            <div style={{ whiteSpace: "pre-wrap" }}>
              {isExpanded ? text : text.slice(0, 100)}
              {text.length > 100 && !isExpanded && "..."}
            </div>
            {showToggle && (
              <Button
                variant="link"
                size="sm"
                className="p-0 view-more-text"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRowExpand(row.taskID, 'commentsExpanded');
                }}
              >
                {isExpanded ? "View Less" : "View More"}
              </Button>
            )}
          </div>
        );
      },
        width: '240px',

    style: {
      borderRight: '1px solid #dee2e6',
        textAlign:'left'
    },
    },
    {
      name: "Action",
      cell: (row) => (
        <button 
          className="btn btn-outline-success archiveicon-btn" 
          variant="outline-success custompadding"  
          size="sm me-2"
          onClick={() => {
            setTaskID(row.taskID);
            setShowModal(true);
          }}
          style={{ cursor: "pointer" }}
        >
          <i className="bi bi-arrow-return-right"></i>
        </button>
      ),
       width: '100px',
       center:true,

    style: {
      borderRight: '1px solid #dee2e6',
      
    },
      ignoreRowClick: true,
      allowOverflow: true,
      
    },
  ];

  const fetchActionComments = async (taskID) => {
    if (!taskID || isNaN(taskID)) {
      toast.error("Invalid Task ID");
      return;
    }

    try {
      setActionCommentsModal((prev) => ({ ...prev, loading: true }));

      const response = await apiGet(`/TaskTracking/ActionComments/${taskID}`);
      if (response) {
        const formattedComments = response
          .map((comment) => ({
            ...comment,
            createdDate: comment.createdDate
              ? new Date(comment.createdDate)
              : new Date(),
          }))
          .sort((a, b) => b.createdDate - a.createdDate);

        setActionCommentsModal((prev) => ({
          ...prev,
          comments: formattedComments,
          loading: false,
        }));
      } else {
        setActionCommentsModal((prev) => ({ ...prev, loading: false }));
        toast.error(response?.errors?.join(", ") || "No action comments found");
      }
    } catch (error) {
      console.error("Error fetching action comments:", error);
      setActionCommentsModal((prev) => ({ ...prev, loading: false }));
      const errorMsg =
        error.response?.data?.title ||
        error.response?.data?.errors?.taskID?.join(", ") ||
        "Failed to load action comments";
      toast.error(errorMsg);
    }
  };

  const handleShowActionComments = (task) => {
    if (!task?.taskID) {
      toast.error("Invalid task data");
      return;
    }

    setActionCommentsModal({
      show: true,
      taskId: task.taskID,
      comments: [],
      loading: true,
      coordinatorName: task.coordinatorNames || "Coordinator",
    });

    fetchActionComments(task.taskID);
  };

  const filteredTasks = useMemo(() => {
    if (!filterText) return tasks;
    return tasks.filter((task) =>
      Object.values(task).some((value) =>
        value?.toString().toLowerCase().includes(filterText.toLowerCase())
      )
    );
  }, [filterText, tasks]);

  return (
    <div className="main-content">
      <div className="container">
        <h2 className="text-xl font-bold mb-4 page-heading">Deleted Task</h2>

        <div className="py-2 px-0 border-0 bg-white mb-custom">
          <div className="mt-4 mb-4">
            <Form.Control
              type="text"
              placeholder="Search inactive tasks..."
              onChange={(e) => setFilterText(e.target.value)}
              style={{ width: "300px" }}
                 className=" searchinputdash"
            />{" "}
            {filterText && (
              <small className="ms-2 text-muted">
                Search result - {filteredTasks.length}
              </small>
            )}
          </div>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="btn-primary" />
              <p className="mt-2">Loading inactive tasks...</p>
            </div>
          ) : (
            <div className="card shadow-sm rounded-4 py-3 px-0 border-0 bg-white mb-custom">
              <DataTable
                columns={columns}
             
                          data={filteredTasks.map((dept, idx) => ({
  ...dept,
    _rowIndex: idx, 
  }))}
                pagination
             
                striped
                responsive
      className="p-datatable-striped customuser-data-table customarchive-data-table   dashboard-table"
      
              customStyles={{
              table: {
                style: {
             
      fontWeight: '600',
      fontSize: '14px',
      backgroundColor: '#f8f9fa',
      textAlign: 'center',
                },
              },
              headRow: {
                style: {
                  backgroundColor: "#04364A!important",
                  minHeight: "48px",
                },
              },
              headCells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "white",
                  textTransform: "uppercase",
                   textAlign: columns.name === "Issue/Action/Decision" ? "center" : "left",
                },
              },
              rows: {
                style: {
                  borderBottom: "1px solid #fdfdfd",
                  "&:hover": {
                    backgroundColor: "#fdfdfd",
                  },
                },
              },
              cells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "14px",
                  color: "#334155",
                },
              },
            }}
            
                                                   conditionalRowStyles={[
     {
    when: (row) => row._rowIndex % 2 === 0, 
    style: { backgroundColor: '#f2f2f2' },
  },
  {
    when: (row) => row._rowIndex % 2 !== 0,
    style: { backgroundColor: '#ffffff' },
  },
  ]}
              />
            </div>
          )}
        </div>
      </div>
      <ActivateTaskModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        handleActivate={handleActivate}
      />
      <Alert
        show={toastConfig.show}
        variant={toastConfig.type}
        onClose={hideToast}
        dismissible
        className="position-fixed bottom-0 end-0 m-3"
        style={{ zIndex: 1050, minWidth: "300px" }}
      >
        {toastConfig.message}
      </Alert>
      <CommentsModal
        show={commentsModal.show}
        onHide={() => setCommentsModal((prev) => ({ ...prev, show: false }))}
        taskId={commentsModal.taskId}
        type={commentsModal.type}
        officerName={commentsModal.officerName}
        coordinatorName={commentsModal.coordinatorName}
      />

      <AttachmentsModalTrash
        show={attachmentsModal.show}
        onHide={() => setAttachmentsModal((prev) => ({ ...prev, show: false }))}
        taskId={attachmentsModal.taskId}
      />

      <DeadlineHistoryModal
        show={deadlineHistoryModal.visible}
        onHide={() =>
          setDeadlineHistoryModal((prev) => ({ ...prev, visible: false }))
        }
        deadlines={deadlineHistoryModal.deadlines}
        loading={deadlineHistoryModal.loading}
        taskId={deadlineHistoryModal.currentTaskId}
      />

      <ActionCommentsModal
        show={actionCommentsModal.show}
        onHide={() =>
          setActionCommentsModal((prev) => ({ ...prev, show: false }))
        }
        taskId={actionCommentsModal.taskId}
        comments={actionCommentsModal.comments}
        loading={actionCommentsModal.loading}
        coordinatorName={actionCommentsModal.coordinatorName}
        fetchActionComments={fetchActionComments}
      />
    </div>
  );
};

export default Trash;